/*
 * 文件加载 Hook
 *
 * 统一处理「本地 File」和「远程 URL」两种来源，
 * 输出 ArrayBuffer，给 docx-preview / ExcelJS 使用。
 *
 * 用法：
 *   const { buffer, loading, error } = useFileLoader(source)
 */

import { useEffect, useState } from 'react'
import type { FileSource } from '../types'

interface FileLoaderResult {
  buffer: ArrayBuffer | null
  loading: boolean
  error: Error | null
  loadedSourceKey: string | null
  progress: number | null
}

const remoteBufferCache = new Map<string, Promise<ArrayBuffer>>()
const localFileBufferCache = new WeakMap<File, Promise<ArrayBuffer>>()

export async function loadFileBuffer(source: FileSource): Promise<ArrayBuffer> {
  if (typeof source === 'string') {
    const cached = remoteBufferCache.get(source)
    if (cached) return cached

    // URL 模式
    // - 同源 / 我们自己的 BFF (/xhey-ai/...) → 带 cookie
    // - OSS / CDN 等外链 → 不带 cookie（避免 CORS preflight 失败）
    const isExternal = /^https?:\/\//.test(source) && !isSameOrigin(source)
    const request = fetchRemoteBuffer(source, isExternal).catch((err) => {
      remoteBufferCache.delete(source)
      throw err
    })

    remoteBufferCache.set(source, request)
    return request
  }
  // 本地 File 模式
  const cached = localFileBufferCache.get(source)
  if (cached) return cached

  const request = source.arrayBuffer().catch((err) => {
    localFileBufferCache.delete(source)
    throw err
  })
  localFileBufferCache.set(source, request)
  return request
}

export async function loadFileBufferWithProgress(
  source: FileSource,
  onProgress?: (progress: number | null) => void
): Promise<ArrayBuffer> {
  if (typeof source !== 'string') return loadFileBuffer(source)

  const cached = remoteBufferCache.get(source)
  if (cached) {
    if (onProgress) onProgress(1)
    return cached
  }

  const isExternal = /^https?:\/\//.test(source) && !isSameOrigin(source)
  const request = fetchRemoteBuffer(source, isExternal, onProgress).catch(
    (err) => {
      remoteBufferCache.delete(source)
      throw err
    }
  )

  remoteBufferCache.set(source, request)
  return request
}

// 新建空表 + LLM 首轮写入(约 1-3 秒)期间,后端可能短暂返回 404 —— 文件还在写、
// 或 refreshUrl 命中缓存穿透。此时用户手动"刷新预览"就能好,自动化这个行为:
// 遇到 404 按 [500, 1000, 2000, 3000] ms 递增重试,总计约 6.5s 内自动兜住。
// 其他状态(401/500/网络错)不重试,避免异常放大。
const FETCH_404_RETRY_DELAYS_MS = [500, 1000, 2000, 3000]

async function fetchRemoteBuffer(
  source: string,
  isExternal: boolean,
  onProgress?: (progress: number | null) => void
): Promise<ArrayBuffer> {
  let res: Response
  let attempt = 0
  // eslint-disable-next-line no-constant-condition
  while (true) {
    res = await fetch(source, {
      credentials: isExternal ? 'omit' : 'include',
      mode: 'cors'
    })
    if (res.status !== 404 || attempt >= FETCH_404_RETRY_DELAYS_MS.length) break
    const delay = FETCH_404_RETRY_DELAYS_MS[attempt]
    // eslint-disable-next-line no-console
    console.warn(
      `[useFileLoader] 404 on ${source.slice(0, 80)}...(attempt ${attempt + 1}, retry after ${delay}ms)`
    )
    attempt++
    await new Promise((r) => setTimeout(r, delay))
  }
  if (!res.ok) {
    throw new Error(`下载文件失败: ${res.status} ${res.statusText}`)
  }

  const total = parseInt(res.headers.get('content-length') || '0', 10)
  if (!res.body) {
    const buf = await res.arrayBuffer()
    if (onProgress) onProgress(1)
    return buf
  }

  const reader = res.body.getReader()
  const chunks: Uint8Array[] = []
  let received = 0

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    if (!value) continue
    chunks.push(value)
    received += value.byteLength
    if (total > 0) {
      if (onProgress) onProgress(Math.min(received / total, 1))
    }
  }

  const merged = new Uint8Array(received)
  let offset = 0
  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.byteLength
  }
  if (onProgress) onProgress(1)
  return merged.buffer
}

function isSameOrigin(url: string): boolean {
  try {
    const u = new URL(url, window.location.href)
    return u.origin === window.location.origin
  } catch {
    return true
  }
}

/** 从扩展名/URL 自动推断文档类型。
 *  支持三种写法：
 *    1) 普通 URL 直接以 .xlsx/.docx 结尾
 *    2) 带 query 的 OSS 签名 URL：/xxx.xlsx?token=...
 *    3) 后端代理签名 URL：/files/raw-signed?path=uploads/xxx.xlsx&token=...
 */
export function detectDocType(source: FileSource): 'docx' | 'xlsx' | 'unknown' {
  const name = typeof source === 'string' ? source : source.name
  const lower = name.toLowerCase()
  // 1) pathname 直接是扩展名结尾
  if (lower.endsWith('.docx') || lower.endsWith('.doc')) return 'docx'
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return 'xlsx'
  // 2) query 参数里带文件路径（path=xxx.xlsx / file=xxx.docx）
  const inQuery = lower.match(
    /[?&](?:path|file|filename|name)=([^&]*\.(docx?|xlsx?))/
  )
  if (inQuery) {
    return inQuery[2].startsWith('xls') ? 'xlsx' : 'docx'
  }
  // 3) URL 里 ".xlsx" / ".docx" 后接 ?/&/# 而不是 = 号
  if (/\.docx?(?:[?&#]|$)/.test(lower)) return 'docx'
  if (/\.xlsx?(?:[?&#]|$)/.test(lower)) return 'xlsx'
  return 'unknown'
}

export function getFileSourceKey(
  source: FileSource | null | undefined
): string | null {
  if (!source) return null
  return typeof source === 'string' ? source : source.name
}

export function useFileLoader(
  source: FileSource | null | undefined
): FileLoaderResult {
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [loadedSourceKey, setLoadedSourceKey] = useState<string | null>(null)
  const [progress, setProgress] = useState<number | null>(null)

  useEffect(() => {
    if (!source) {
      setBuffer(null)
      setLoading(false)
      setError(null)
      setLoadedSourceKey(null)
      setProgress(null)
      return
    }

    let cancelled = false
    const sourceKey = getFileSourceKey(source)
    setLoading(true)
    setError(null)
    setProgress(typeof source === 'string' ? 0 : null)

    loadFileBufferWithProgress(source, (nextProgress) => {
      if (!cancelled) setProgress(nextProgress)
    })
      .then((buf) => {
        if (!cancelled) {
          setBuffer(buf)
          setLoadedSourceKey(sourceKey)
          setProgress(1)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
          setProgress(null)
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [source])

  return { buffer, loading, error, loadedSourceKey, progress }
}
